import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Loader2, Save, ArrowUp, ArrowDown, Lock } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { v: "text", n: "Tekst" },
  { v: "number", n: "Liczba" },
  { v: "email", n: "E-mail" },
  { v: "textarea", n: "Długi tekst" },
  { v: "select", n: "Lista wyboru" },
];

export default function AdminEventForm() {
  const [form, setForm] = useState({ title: "", description: "", fields: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/event-reg-form").then((r) => setForm(r.data)).catch(() => {}); }, []);

  const addField = () => {
    const id = `field_${Date.now()}`;
    setForm((s) => ({ ...s, fields: [...s.fields, { id, label: "Nowe pole", type: "text", required: false, options: [] }] }));
  };
  const updateField = (i, patch) => setForm((s) => { const fields = [...s.fields]; fields[i] = { ...fields[i], ...patch }; return { ...s, fields }; });
  const removeField = (i) => setForm((s) => ({ ...s, fields: s.fields.filter((_, idx) => idx !== i) }));
  const move = (i, d) => setForm((s) => { const fields = [...s.fields]; const j = i + d; if (j < 0 || j >= fields.length) return s; [fields[i], fields[j]] = [fields[j], fields[i]]; return { ...s, fields }; });

  const save = async () => {
    setSaving(true);
    try { await api.put("/event-reg-form", form); toast.success("Formularz zapisany"); }
    catch { toast.error("Błąd zapisu"); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Formularz „Weź udział"</h2>
          <p className="mt-1 text-sm text-muted-foreground">Edytuj pola formularza zgłoszenia na konferencje i dodawaj własne pola. Pole „Adres e-mail" jest wymagane i nie można go usunąć.</p>
        </div>
        <Button onClick={save} disabled={saving} className="gap-1.5 bg-ztk-blue" data-testid="eventform-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Zapisz
        </Button>
      </div>

      <div className="mt-8 max-w-2xl space-y-4">
        <div className="rounded-2xl border border-border bg-card p-6">
          <div><Label>Tytuł formularza</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="eventform-title" className="mt-1.5" /></div>
          <div className="mt-4"><Label>Opis (widoczny nad polami)</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="eventform-desc" className="mt-1.5" /></div>
        </div>

        <div className="space-y-3">
          {(form.fields || []).map((f, i) => {
            const locked = f.id === "email";
            return (
              <div key={f.id} className="rounded-xl border border-border bg-card p-4" data-testid={`eventform-field-${i}`}>
                <div className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button type="button" onClick={() => move(i, -1)} className="text-muted-foreground hover:text-ztk-blue" data-testid={`eventform-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => move(i, 1)} className="text-muted-foreground hover:text-ztk-blue" data-testid={`eventform-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                  </div>
                  <Input value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} placeholder="Etykieta" className="flex-1" data-testid={`eventform-label-${i}`} />
                  <select value={f.type} onChange={(e) => updateField(i, { type: e.target.value })} disabled={locked} className="h-10 rounded-md border border-input bg-background px-2 text-sm disabled:opacity-60" data-testid={`eventform-type-${i}`}>
                    {TYPES.map((t) => <option key={t.v} value={t.v}>{t.n}</option>)}
                  </select>
                  {locked ? (
                    <span className="flex h-9 w-9 items-center justify-center text-muted-foreground" title="Pole stałe"><Lock className="h-4 w-4" /></span>
                  ) : (
                    <Button size="icon" variant="ghost" onClick={() => removeField(i)} className="text-destructive" data-testid={`eventform-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
                {f.type === "select" && (
                  <div className="mt-2 pl-8">
                    <Label className="text-xs">Opcje listy (oddziel przecinkami)</Label>
                    <Input value={(f.options || []).join(", ")} onChange={(e) => updateField(i, { options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="np. Tak, Nie, Może" className="mt-1" data-testid={`eventform-options-${i}`} />
                  </div>
                )}
                <div className="mt-2 flex items-center gap-2 pl-8">
                  <Switch checked={f.required} onCheckedChange={(v) => updateField(i, { required: v })} disabled={locked} data-testid={`eventform-required-${i}`} />
                  <span className="text-sm text-muted-foreground">Pole wymagane</span>
                </div>
              </div>
            );
          })}
        </div>
        <Button variant="outline" onClick={addField} className="gap-1.5" data-testid="eventform-add-field"><Plus className="h-4 w-4" /> Dodaj pole</Button>
      </div>
    </div>
  );
}
